// src/panels/ServerSelectPanel.jsx
import React, { useMemo, useState, useRef, useEffect } from "react";
import MuldeFrame from "./MuldeFrame";

/* ---------- Mini-SVG: stroke ODER fill ---------- */
function InlineIcon({ d, viewBox="0 0 24 24", size=18, mode="stroke", strokeWidth=2 }) {
  if (!d) return null;
  const common = { width:size, height:size, viewBox, style:{display:"block"}, "aria-hidden":true, focusable:"false" };
  return mode === "stroke"
    ? <svg {...common} fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><path d={d} vectorEffect="non-scaling-stroke"/></svg>
    : <svg {...common} fill="currentColor" fillRule="evenodd" clipRule="evenodd"><path d={d}/></svg>;
}

/* ---------- Flat Button: LED | ICON | TEXT ---------- */
function FlatBtnLED({ label, icon, ledOn=false, onClick }) {
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [kbd, setKbd] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      onKeyDown={(e) => { const k=e.key; if (k==="Tab"||k==="Shift"||k.startsWith("Arrow")) setKbd(true); }}
      onPointerDown={() => setKbd(false)}
      style={{
        all:"unset",
        display:"inline-flex", alignItems:"center", gap:3,
        height:28, padding:"0 5px",
        boxSizing:"border-box", background:"transparent",
        border:`1px solid ${hover ? "rgba(255,136,0,.9)" : "rgba(255,255,255,.18)"}`,
        borderRadius:5, color:"#e6edf3", fontSize: "var(--fs-16, 16px)", lineHeight:1, cursor:"pointer",
        outline: kbd && focus ? "1px dashed #fff" : "none",
        outlineOffset: kbd && focus ? 2 : 0,
        maxWidth:"100%", minWidth:0,
      }}
    >
      <span aria-hidden style={{
        width:8, height:8, borderRadius:9999, flexShrink:0,
        background: ledOn
          ? "radial-gradient(circle at 50% 50%, #FF8800 0%, #FF8800 45%, #0b1a12 75%, #0a131e 100%)"
          : "radial-gradient(circle at 30% 30%, #0f1622 0%, #0b1220 60%, #070e1a 100%)",
        boxShadow: ledOn
          ? "inset 0 1px 1px rgba(255,255,255,.25), inset 0 -1px 1px rgba(0,0,0,.6), 0 0 4px rgba(255,136,0,.55)"
          : "inset 0 1px 1px rgba(255,255,255,.15), inset 0 -1px 1px rgba(0,0,0,.6), 0 0 0 1px rgba(0,0,0,.35)",
      }}/>
      {icon && <span style={{ display:"block", flexShrink:0, transform:"translateY(0.5px)" }}>{icon}</span>}
      <span style={{ whiteSpace:"nowrap", transform:"translateY(-0.5px)" }}>{label}</span>
    </button>
  );
}

/* ---------- Dünne Scrollbar nur hier ---------- */
const ThinScrollStyles = () => (
  <style>{`
    .thin-scroll::-webkit-scrollbar { width: 6px; }
    .thin-scroll::-webkit-scrollbar-track { background: rgba(0,0,0,.15); border-radius: 6px; }
    .thin-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,.2); border-radius: 6px; }
    .thin-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,.35); }
  `}</style>
);

/* ---------- zentriertes „Deluxe“-Modal (Dummy) ---------- */
function CenterModal({ open, onClose, onConfirm, width=500, height=300, children }) {
  if (!open) return null;
  return (
    <div role="dialog" aria-modal="true" style={{ position:"fixed", inset:0, display:"grid", placeItems:"center", background:"rgba(0,0,0,.45)", zIndex:1000 }}>
      <div style={{
        width, height, borderRadius:12, background:"rgba(10,12,18,.95)",
        border:"1px solid rgba(195,208,218,.45)",
        boxShadow:"0 10px 40px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.05)",
        color:"#e6edf3", display:"grid", gridTemplateRows:"auto 1fr auto", overflow:"hidden",
      }}>
        <div style={{ padding:"0px 12px", fontWeight:700, letterSpacing:".06em", textAlign:"center" }}>DELUXE – Serverauswahl</div>
        <div style={{ padding:12, overflow:"auto" }} className="thin-scroll">{children}</div>
        <div style={{ padding:10, display:"flex", gap:8, justifyContent:"flex-end" }}>
          <button onClick={onClose} style={btnGhost}>Abbruch</button>
          <button onClick={onConfirm} style={btnAccent}>Bestätigen</button>
        </div>
      </div>
      <ThinScrollStyles/>
    </div>
  );
}
const btnGhost = { all:"unset", padding:"6px 0px", borderRadius:6, border:"1px solid rgba(255,255,255,.25)", cursor:"pointer", userSelect:"none" };
const btnAccent= { all:"unset", padding:"6px 0px", borderRadius:6, border:"1px solid rgba(255,136,0,.9)", background:"rgba(255,136,0,.1)", cursor:"pointer", userSelect:"none" };

/* ======================================================= */
/*                         PANEL                            */
/* ======================================================= */
export default function ServerSelectPanel({ selected, onSelect, isLoading }) {
  // Dummy-Daten + echter Parser
  const [servers, setServers] = useState([]);

  const isLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  useEffect(() => {
    if (isLocal) {
      // Lokale Dummy-Liste für deine Tests
      setServers([
        "local-seed",
        "ts21.x2.international",
        "lusobr.x1.lusobrasileiro",
        "eternos.x1.hispano",
        "ts21.x2.international",
        "ts21.x2.international",
        "ts21.x2.international",
        "ts21.x2.international",
        "ts30.x5.international",
      ]);
      return;
    }

    (async () => {
      try {
        const res = await fetch("https://www.legendstools.de/api/worlds.php");
        if (!res.ok) throw new Error("Worlds API failed");
        const data = await res.json();
        // Wir nehmen erst mal nur den Code
        setServers(data.map(w => w.code));
      } catch (err) {
        console.error("Worlds fetch error", err);
        setServers([]); // im Zweifel leer
      }
    })();
  }, [isLocal]);

  const [ddOpen, setDdOpen] = useState(false);
  const [deluxeOpen, setDeluxeOpen] = useState(false);

  const [pending, setPending] = useState("");

  const panelRef = useRef(null);
  const rowRef   = useRef(null);
  const [ddTop, setDdTop] = useState(0);

  useEffect(() => {
    const recalc = () => {
      if (!rowRef.current || !panelRef.current) return;
      const row = rowRef.current.getBoundingClientRect();
      const pan = panelRef.current.getBoundingClientRect();
      setDdTop(row.bottom - pan.top + 6);
    };
    recalc();
    if (ddOpen) {
      window.addEventListener("resize", recalc);
      const id = requestAnimationFrame(recalc);
      return () => { window.removeEventListener("resize", recalc); cancelAnimationFrame(id); };
    }
  }, [ddOpen]);

  const dServer = "M10 15h12v1H10zm-4-3h2v-2H6zm4-1h12v-1H10zm-4 6h2v-2H6zm4 4h12v-1H10zm-4 1h2v-2H6zM23 3v3a1.001 1.001 0 0 1-1 1H2a1.001 1.001 0 0 1-1-1V3a1.001 1.001 0 0 1 1-1h20a1.001 1.001 0 0 1 1 1zm-.999 3H22V3H2v3h20.001z";
  const dDeluxe = "M8 21H20.4C20.7314 21 21 20.7314 21 20.4V3.6C21 3.26863 20.7314 3 20.4 3H3.6C3.26863 3 3 3.26863 3 3.6V16 M3.5 20.5L12 12M12 12V16M12 12H8";

  return (
    <MuldeFrame
    title="SERVERAUSWAHL"
    titleStyle={{ marginTop: 5, marginBottom: -10, color: "#8fa8e8", textAlign: "center", fontSize: 16 }}
    style={{}}   // KEIN padding hier!
  >
   
      {/* EIN zentraler Innen-Wrapper = dein einziger Ort für Abstände */}
      <div
        ref={panelRef}                  // <- wichtig für ddTop-Berechnung
        style={{
          padding: 12,                  // <- hier kommt dein Innenabstand hin
          display: "grid",
          gap: 8,                       // <- Buttons-Abstand wie Menüs
          minWidth: 0,
          position: "relative",         // <- damit das Dropdown absolut dazu passt
        }}
      >
        
        {/* Buttons */}
        <div
          ref={rowRef}
          style={{
            display:"grid",
            gridTemplateColumns:"1fr 1fr",
            gap:8,                 // exakt 8px
            alignItems:"center",
            marginBottom:10,
            minWidth:0,
          }}
        >
          <FlatBtnLED
            label="Liste"
            ledOn={ddOpen}
            onClick={() => setDdOpen(v => !v)}
            icon={<InlineIcon d={dServer} viewBox="0 0 24 24" mode="fill" size={18} />}
          />
          <FlatBtnLED
            label="Tabelle"
            ledOn={deluxeOpen}
            onClick={() => { setPending(selected || ""); setDeluxeOpen(true); }}
            icon={<InlineIcon d={dDeluxe} viewBox="0 0 24 24" mode="stroke" size={18} strokeWidth={1} />}
          />
        </div>

        {/* Panel-breites Dropdown */}
        {ddOpen && (
          <div
            style={{
              position:"absolute",
              left:12, right:12, top:ddTop,                 // bündig zum Innenpadding 10
              maxWidth:"calc(100%)",
              borderRadius:10,
              background:"rgba(8,10,16,.85)",
              border:"1px solid rgba(195,208,218,.38)",
              boxShadow:"0 8px 24px rgba(0,0,0,.6)",
              maxHeight:240,
              overflow:"auto",
              zIndex:20,
            }}
            className="thin-scroll"
          >
            <div style={{ padding: 5 }}>
              <input
                placeholder="Suche…"
                onChange={() => {}}
                style={{
                  all:"unset", width:"90%", padding:"3px 4px",
                  border:"1px solid rgba(255,255,255,.18)", borderRadius:4, fontSize:18,
                }}
              />
            </div>
            <ul style={{ listStyle:"none", margin:0, padding:4, display:"grid", gap:1, fontSize:18 }}>
              {servers.map((s, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => { onSelect?.(s); setDdOpen(false); }}
                    style={{ all:"unset", display:"block", width:"90%", padding:"1px 4px", borderRadius:6, cursor:"pointer", border:"1px solid transparent" }}
                    onPointerEnter={(e)=> e.currentTarget.style.border="1px solid rgba(255,255,255,.28)"}
                    onPointerLeave={(e)=> e.currentTarget.style.border="1px solid transparent"}
                  >
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* kleine Sektion-Überschrift */}
        <div style={{
          marginTop:-5, marginBottom:-5,
          color:"rgb(143, 168, 232)",
          letterSpacing:".18em",
          fontWeight:700,
          fontSize:16,                    // 10px
          textTransform:"uppercase",
          userSelect:"none",
        }}>
          SERVER | WELT
        </div>

        {/* Display */}
        <div
          title={selected || ""}
          style={{
            width:"100%", maxWidth:"100%", minWidth:0, boxSizing:"border-box",
            height:30, borderRadius:5,
            background:"linear-gradient(165deg,#4f1818 50%, #4f1818 70%)",
            display:"grid", justifyItems:"center", alignItems:"center",
            padding:"3px 10px",
            fontFamily:"DisplayBadge", lineHeight:1.15,
            border:"2px solid rgba(0,0,0,1)",
            boxShadow:[
              "inset 0 0px 8px 10px rgba(0,0,0,.55)",
              "inset 0 2px 4px rgba(10,10,10,.20)",
              "inset 0px -1px 0px rgba(255,255,255,.25)",
              "inset 1px -1px 0px rgba(255,255,255,.15)",
              "inset -1px -1px 0px rgba(255,255,255,.15)",
              "0 0 1px 1px rgba(0,0,0,.6)",
              "0 -1px 0px 0px rgba(255,255,255,.2)",
              "0 0 3px rgba(96,140,255,.10)",
              "0 0 3px rgba(96,140,255,.10)"
            ].join(", "),
            color:"#bf0d0d", fontSize:18, fontWeight:400,
            textShadow:"0 0 6px rgba(100,13,13,.10), 0 0 1px rgba(37,9,9,.40)",
            letterSpacing:"0.03em", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
          }}
        >{isLoading
          ? "LOADING..."
          : (selected || "⇧    Server auswählen    ⇧")}
        
        </div>
      </div>

      {/* Modal (Dummy) */}
      <CenterModal
        open={deluxeOpen}
        onClose={() => setDeluxeOpen(false)}
        onConfirm={() => { if (pending) onSelect?.(pending); setDeluxeOpen(false); }}
        width={520} height={320}
      >
        <div style={{ display:"grid", gap:8 }}>
          {servers.map((s, i) => (
            <label key={i} style={{
              display:"flex", alignItems:"center", gap:8, padding:"4px 6px",
              borderRadius:6, border:"1px solid rgba(255,255,255,.12)", cursor:"pointer",
            }}
            onPointerEnter={(e)=> e.currentTarget.style.border="1px solid rgba(255,255,255,.28)"}
            onPointerLeave={(e)=> e.currentTarget.style.border="1px solid rgba(255,255,255,.12)"}>
              <input type="radio" name="server" checked={pending === s} onChange={() => setPending(s)} style={{ accentColor:"#FF8800", cursor:"pointer" }}/>
              <span style={{ fontSize:13 }}>{s}</span>
            </label>
          ))}
        </div>
      </CenterModal>

      <ThinScrollStyles/>
    </MuldeFrame>
  );
}
